// Copyright (c) Docugami, Inc. All rights reserved.

import { Checkbox, Spinner, TooltipHost, TooltipOverflowMode } from '@fluentui/react';
import { DisabledCss } from 'components/common/DgCommonStyledComponents';
import { disabledDarkGreyText, emptyGray, primaryThemeSettings } from 'components/shell/presentation/AppStyles';
import { ConfirmationDialog } from 'components/shell/utils/ConfirmationDialog';
import { PickerContainer } from 'components/work_path/WorkPathStyles';
import { useNavigationService } from 'hooks/navigationService';
import { useAppSelector } from 'hooks/redux';
import { DocSetCollectionType, WorkPathType } from 'networking/WorkPathApi';
import { useEffect, useMemo, useState } from 'react';
import { SampleStatus } from 'store/clusters/samplesApi';
import { createSelectGetSamplesState, createSelectOrgSampleClusters, selectAllSamples, useGetCatalogSamplesQuery, useGetOrganizationSamplesQuery } from 'store/clusters/samplesSlice';
import { getIsDocSetInstalling, mapVisibleUserClusters } from 'store/state_helpers';
import styled from 'styled-components';
import { DocSetSelectedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { useAssistedAuthoringDocSetStatus } from 'utils/AssistedAuthoringUtils';
import { ClusterInfo } from '../../../networking/api';
import { dgFontFamily, SectionalUI, userFontFamily } from '../../common/Fonts';
import { DocSetStatus, DocSetStatusInfo, useDocSetStatus } from '../DocSetStatus';

const DocSetsListContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: 20px 5px;
    overflow-y: auto;
    overflow-x: hidden;
    border: solid;
    border-color: ${emptyGray};
    padding: 20px;
    gap: 40px;
`;

const DocSetsListItems = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 15px;
`;

const DocSetsListItem = styled.div<{ hideDivider?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 55px;
    padding: 5px 0;
    align-items: flex-start;
    justify-content: center;
    border-bottom: ${props => props.hideDivider ? 'initial' : '1px solid grey'};
`;

const DocSetItemContainer = styled.div<{ disabled?: boolean }>`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0;
    box-sizing: border-box;
    gap: 5px;
    .ms-TooltipHost {
        overflow: hidden;
        text-overflow: ellipsis;
    }
    :hover {
        cursor: ${({ disabled }) => disabled ? 'inherit' : 'pointer'};
    }
`;

const DocSetItemText = styled.span<{ disabled?: boolean }>`
    text-align: left;
    font-family: ${userFontFamily};
    font-weight: 700;
    font-size: 20px;
    line-height: 24px;
    color: ${({ disabled }) => disabled ? disabledDarkGreyText : primaryThemeSettings.palette.black};
    white-space: nowrap;
`;

const LinkItemText = styled.a<{ disabled?: boolean, hidden?: boolean }>`
    text-align: center;
    display: ${props => props.hidden ? 'none' : 'flex'};
    place-self: center;
    font-family: ${dgFontFamily};
    font-weight: 400;
    font-style: italic;
    font-size: 20px;
    line-height: 24px;
    ${({ disabled }) => disabled && DisabledCss};
    white-space: nowrap;
    cursor: pointer;

    :hover {
        text-decoration: underline;
    }
`;

const StatusIndicator = styled.div<{ color?: string }>`
    margin-left: auto;
    margin-right: 10px;
    font-style: italic;
    color: ${props => props.color};
`;

interface DocSetPickerProps {
    defaultSelect?: boolean;
    onSelected?: (id: string) => void;
    targetDocSet?: string;
    disabled?: boolean;
    collectionFilter?: DocSetCollectionType;
    className?: string;
    workPathType?: WorkPathType;
}

/**
 * Component used for picking a specific doc set
 * @param props
 */
const selectGetSamplesState = createSelectGetSamplesState()
const selectOrgSampleClusters = createSelectOrgSampleClusters();
export const DocSetPicker = (props: DocSetPickerProps): JSX.Element => {
    const [selectedClusterId, setSelectedClusterId] = useState('');
    const [showBrowseSamplesDialog, setShowBrowseSamplesDialog] = useState(false);

    const userDocSets = useAppSelector(state => mapVisibleUserClusters(state.clusters));
    const sampleDocSets = useAppSelector(state => selectOrgSampleClusters(state));
    const sampleStates = useAppSelector(state => selectAllSamples(selectGetSamplesState(state)));
    const isFetched = useAppSelector(state => state.clusters.fetched);

    const { isLoading: catalogSamplesLoading } = useGetCatalogSamplesQuery();
    const { isLoading: organizationSamplesLoading } = useGetOrganizationSamplesQuery();

    const selectedClusterValidity = useDocSetValidity(selectedClusterId, props.workPathType);

    const { navToBrowseSamplesPage } = useNavigationService();

    const showSampleCatalogLink = useMemo(() => {
        return !catalogSamplesLoading && !!sampleStates.find(sample => sample.status === SampleStatus.Uninstalled)
    }, [catalogSamplesLoading, sampleStates])

    function onDocSetSelected(id: string) {
        if (props.disabled) {
            return;
        }

        setSelectedClusterId(id);
    }

    function isCollectionVisible(type: DocSetCollectionType): boolean {
        return !props.collectionFilter || props.collectionFilter === type || props.collectionFilter === DocSetCollectionType.All;
    }

    useEffect(() => {
        if (!isFetched || organizationSamplesLoading || props.targetDocSet === selectedClusterId) {
            return;
        }

        function tryFindDocSet(id: string) {
            let index = userDocSets.findIndex(x => x.clusterId === id);
            if (index === -1) {
                index = sampleDocSets.findIndex(x => x.clusterId === id);
            }

            return index !== -1;
        }

        // Attempt to select the target doc set
        if (!!props.targetDocSet && tryFindDocSet(props.targetDocSet)) {
            setSelectedClusterId(props.targetDocSet);
        }

    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFetched, props.targetDocSet, organizationSamplesLoading])

    // Handle default selection logic
    useEffect(() => {
        // If we haven't fetched doc sets or we've already selected something, then
        // we don't apply default selection logic
        if (!isFetched || organizationSamplesLoading || !!selectedClusterId) {
            return;
        }

        let targetDocSetId = '';
        if (!!props.defaultSelect) {
            if (userDocSets.length > 0 && isCollectionVisible(DocSetCollectionType.User)) {
                targetDocSetId = userDocSets[0].clusterId!;
            }
            else if (sampleDocSets.length > 0 && isCollectionVisible(DocSetCollectionType.Sample)) {
                targetDocSetId = sampleDocSets[0].clusterId!;
            }
        }

        if (!!targetDocSetId) {
            setSelectedClusterId(targetDocSetId);
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFetched, organizationSamplesLoading]);

    useEffect(() => {
        if (!!selectedClusterId && !!props.onSelected) {
            if (!selectedClusterValidity.isValid || selectedClusterValidity.isLoading) {
                // We don't have a valid cluster selected yet
                props.onSelected('');
            } else {
                props.onSelected(selectedClusterId);
            }
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedClusterValidity]);

    useEffect(() => {
        if (!selectedClusterValidity.isLoading) {
            // Log selected docset when we have finished loading state
            Telemetry.trackEvent<DocSetSelectedSchema>(
                {
                    selectedDocSetId: selectedClusterId,
                    isDocSetValid: selectedClusterValidity.isValid,
                    eventName: TelemetryEvent.DocSetSelected
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClusterValidity.isLoading])

    return (
        <PickerContainer className={props?.className}>
            <DocSetsListContainer>
                {isCollectionVisible(DocSetCollectionType.User) && <DocSetCollection docSets={userDocSets} header="My Doc Sets" onSelected={onDocSetSelected} selectedId={selectedClusterId} disabled={props.disabled} selectedDocSetValidity={selectedClusterValidity} />}
                <div>
                    {isCollectionVisible(DocSetCollectionType.Sample) && (
                        <>
                            <DocSetCollection docSets={sampleDocSets} header="Sample Doc Sets" onSelected={onDocSetSelected} selectedId={selectedClusterId} disabled={props.disabled} selectedDocSetValidity={selectedClusterValidity} />
                            <DocSetsListItem hideDivider>
                                <LinkItemText
                                    hidden={!showSampleCatalogLink}
                                    onClick={() => setShowBrowseSamplesDialog(true)}
                                >
                                    {!sampleDocSets.length ? 'Click here to browse sample doc sets and get started!' : 'Click here to browse more samples!'}
                                </LinkItemText>
                            </DocSetsListItem>
                        </>
                    )}
                </div>
            </DocSetsListContainer>
            <ConfirmationDialog
                hidden={!showBrowseSamplesDialog}
                title="Go To Samples Library"
                subText={`Navigating to the samples library will leave this project page with unsaved changes. Click OK to confirm.`}
                confirmButtonText="OK"
                dismissButtonText="Cancel"
                dismissedCallback={() => setShowBrowseSamplesDialog(false)}
                confirmedCallback={navToBrowseSamplesPage} />
        </PickerContainer>
    );
}

interface DocSetItemProps {
    docSetInfo: ClusterInfo;
    onSelected: () => void;
    isSelected: boolean;
    disabled?: boolean;
    statusVisual?: JSX.Element;
}

const DocSetItem = ({ docSetInfo, onSelected, isSelected, disabled, statusVisual }: DocSetItemProps): JSX.Element => {
    const name = useMemo(() => docSetInfo.label!, [docSetInfo.label])
    return (
        <DocSetItemContainer onClick={() => { if (!disabled) onSelected() }} disabled={disabled}>
            <Checkbox checked={isSelected} disabled={disabled} />
            <TooltipHost content={name} overflowMode={TooltipOverflowMode.Self}>
                <DocSetItemText disabled={disabled}>{name.toLocaleUpperCase()}</DocSetItemText>
            </TooltipHost>
            {statusVisual}
        </DocSetItemContainer>
    )
}

interface DocSetCollectionProps {
    docSets: ClusterInfo[];
    header: string;
    selectedId: string;
    onSelected: (id: string) => void;
    disabled?: boolean;
    selectedDocSetValidity: DocSetSelectionValidity;
}

const DocSetCollection = (props: DocSetCollectionProps): JSX.Element => {
    return props.docSets.length > 0 ? (
        <div key={props.header}>
            <SectionalUI>{props.header.toUpperCase()}</SectionalUI>
            <DocSetsListItems>
                {props.docSets.map(docSet => {
                    const isSelected = docSet.clusterId === props.selectedId;
                    return (
                        <DocSetsListItem key={docSet.clusterId!}>
                            {isSelected ? <SelectedDocSetItem docSetInfo={docSet} selectedDocSetValidity={props.selectedDocSetValidity} /> :
                                <DocSetItem docSetInfo={docSet} isSelected={isSelected} onSelected={() => props.onSelected(docSet.clusterId!)} />}
                        </DocSetsListItem>
                    )
                })}
            </DocSetsListItems>
        </div>
    ) : <></>;
}

interface SelectedDocSetItemProps {
    docSetInfo: ClusterInfo;
    selectedDocSetValidity: DocSetSelectionValidity;
}

const SelectedDocSetItem = ({ docSetInfo, selectedDocSetValidity }: SelectedDocSetItemProps): JSX.Element => {
    const { isValid, isLoading, status } = selectedDocSetValidity;

    function getVisual() {
        if (isLoading) {
            return <Spinner style={{ marginLeft: '15px' }} />
        }

        const statusIndicatorText = status?.label;
        return <StatusIndicator hidden={isValid || !status}>{statusIndicatorText}</StatusIndicator>
    }

    return (
        <TooltipHost content={!isValid ? status?.details : undefined} styles={{ root: { display: 'flex', height: 'inherit', width: 'inherit' } }}>
            <DocSetItem docSetInfo={docSetInfo} isSelected onSelected={() => { }} disabled={!isValid} statusVisual={getVisual()} />
        </TooltipHost>
    )
}

interface DocSetSelectionValidity {
    isValid: boolean;
    isLoading: boolean;
    status?: DocSetStatusInfo;
}

/**
 * Hook used for initializing and returning the validity/state of a doc set
 * @param clusterId
 */
export function useDocSetValidity(clusterId: string, workPathType?: WorkPathType): DocSetSelectionValidity {
    const assistedAuthoringStatus = useAssistedAuthoringDocSetStatus(clusterId, workPathType !== WorkPathType.AssistedAuthoring);

    let { isLoading, info: statusInfo } = useDocSetStatus(clusterId);
    const isInstalling = useAppSelector(state => getIsDocSetInstalling(state, clusterId)) || statusInfo?.status === DocSetStatus.Copying;

    if (!isLoading && !!assistedAuthoringStatus?.statusInfo && assistedAuthoringStatus.statusInfo?.status !== DocSetStatus.Ready && statusInfo?.status === DocSetStatus.Ready) {
        // Only replace the existing doc set status if there was no previous error
        statusInfo = assistedAuthoringStatus.statusInfo;
        isLoading ||= assistedAuthoringStatus.isLoading;
    }

    return {
        isValid: statusInfo?.status === DocSetStatus.Ready && !isInstalling,
        isLoading: isLoading,
        status: statusInfo
    }
}
